import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Table2, BookOpen, Brain, Users, AlertCircle } from "lucide-react";
import { FadeUp, EASE } from "@/components/landing/cinematic";

/* ---- Purpose-built artifact micro-visuals (authentic, not icons) ---- */

function ChatPileVisual() {
    return (
        <div className="w-full space-y-2">
            {[
                { who: "Sales", msg: "Price for order #4211?" },
                { who: "Warehouse", msg: "Do we have Rice 25kg?" },
                { who: "Delivery", msg: "Sharma address?" },
            ].map((m) => (
                <div key={m.who} className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white text-[9px] font-bold">
                        {m.who[0]}
                    </div>
                    <div className="flex-1 rounded-lg rounded-tl-sm bg-[#f2ede2] px-2.5 py-1.5 text-[11.5px] text-[#44403c] leading-tight">
                        {m.msg}
                    </div>
                </div>
            ))}
            <div className="pt-1 flex items-center justify-end gap-1.5 text-[10.5px] font-mono text-[#59524a]">
                <span className="inline-flex h-4.5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[9.5px] font-bold text-white">
                    247
                </span>
                unread
            </div>
        </div>
    );
}

function SpreadsheetVisual() {
    const files = [
        { name: "sales_final_v9.xlsx", tint: "bg-emerald-50 border-emerald-200" },
        { name: "stock_current(2).xlsx", tint: "bg-amber-50 border-amber-200" },
        { name: "accounts_2024.xlsx", tint: "bg-blue-50 border-blue-200" },
    ];
    return (
        <div className="relative w-full h-[110px]">
            {files.map((f, i) => (
                <div
                    key={f.name}
                    className={`absolute w-44 rounded-md border ${f.tint} p-2 text-[10.5px] font-mono text-[#44403c] shadow-sm`}
                    style={{ left: `${i * 16}px`, top: `${i * 12}px`, transform: `rotate(${(i - 1) * 2.4}deg)` }}
                >
                    <div className="flex items-center gap-1">
                        <Table2 className="h-2.5 w-2.5 text-[#78716c]" strokeWidth={1.5} />
                        <span className="truncate">{f.name}</span>
                    </div>
                    <div className="mt-1.5 grid grid-cols-3 gap-[2px]">
                        {Array.from({ length: 9 }).map((_, j) => (
                            <div key={j} className="h-1.5 rounded-sm bg-white/70" />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ERPFormVisual() {
    return (
        <div className="w-full rounded-lg border border-[#1c1917]/10 bg-[#faf7f0] p-3 space-y-2">
            {["Customer *", "GSTIN *", "PO ref *", "Item HSN *", "Tax slab *"].map((label, i) => (
                <div key={label} className="flex items-center gap-2.5">
                    <span className="w-16 shrink-0 truncate font-mono text-[10px] text-[#78716c]">{label}</span>
                    <div className={`h-2 flex-1 rounded ${i < 3 ? "bg-red-100" : "bg-[#e8e0d0]"}`} />
                </div>
            ))}
            <div className="flex items-center justify-between pt-1 text-[10.5px]">
                <span className="font-mono text-red-600">5 required · 47 clicks</span>
                <span className="rounded bg-[#1c1917] px-2 py-0.5 text-[9.5px] text-white">Save</span>
            </div>
        </div>
    );
}

function MemoryVisual() {
    return (
        <div className="flex w-full items-center justify-center gap-4 py-2">
            <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#1c1917]/12 bg-white">
                    <Users className="h-7 w-7 text-[#44403c]" strokeWidth={1.4} />
                </div>
                <span className="absolute -top-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#1c1917] text-[12px] font-bold text-white">
                    ?
                </span>
            </div>
            <div className="text-[12px] leading-tight">
                <div className="font-medium text-[#1c1917]">Rakesh knows this.</div>
                <div className="mt-0.5 text-[#78716c]">Rakesh is on leave.</div>
                <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-red-600">
                    <AlertCircle className="h-2.5 w-2.5" strokeWidth={2} /> blocked
                </div>
            </div>
        </div>
    );
}

const PAINS = [
    { id: "owner", icon: MessageCircle, title: "Owner is the bottleneck", line: "Every decision routes through one phone.", visual: <ChatPileVisual /> },
    { id: "visibility", icon: Table2, title: "No shared visibility", line: "Each team lives in its own spreadsheet.", visual: <SpreadsheetVisual /> },
    { id: "erp", icon: BookOpen, title: "ERPs are punishment", line: "A week of training to enter one invoice.", visual: <ERPFormVisual /> },
    { id: "memory", icon: Brain, title: "Memory walks out", line: "Institutional knowledge lives in one head.", visual: <MemoryVisual /> },
];

export default function Problem() {
    const [active, setActive] = useState(0);

    return (
        <section className="relative bg-[#efe8dc] pt-24 lg:pt-36 pb-24 lg:pb-44" data-testid="problem-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-20">
                {/* Sticky narrative rail */}
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-32">
                        <FadeUp>
                            <div className="font-mono text-[11px] font-medium uppercase tracking-[0.26em] text-[#c2410c]">
                                The problem
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <h2 className="mt-4 sm:mt-5 font-display text-[32px] sm:text-5xl lg:text-[54px] font-bold tracking-[-0.03em] text-[#1c1917] leading-[1.05]">
                                Wholesale runs on{" "}
                                <span className="font-editorial font-medium text-[#59524a]">
                                    memory &amp; spreadsheets.
                                </span>
                            </h2>
                        </FadeUp>
                        <FadeUp delay={0.16}>
                            <p className="mt-5 max-w-sm text-[14.5px] sm:text-[15px] leading-[1.7] text-[#59524a]">
                                Four failure modes, watched daily on a real shop floor.
                            </p>
                        </FadeUp>

                        {/* Live counter tied to the card deck */}
                        <div className="mt-12 hidden lg:block" data-testid="problem-counter">
                            <div className="flex items-baseline gap-3">
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={active}
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -14 }}
                                        transition={{ duration: 0.35, ease: EASE }}
                                        className="font-display text-6xl font-bold tracking-tight text-[#1c1917]"
                                    >
                                        0{active + 1}
                                    </motion.span>
                                </AnimatePresence>
                                <span className="font-mono text-[13px] text-[#a29a8e]">/ 04</span>
                            </div>
                            <div className="mt-3 h-px w-40 bg-[#1c1917]/10 relative overflow-hidden">
                                <motion.div
                                    className="absolute inset-y-0 left-0 bg-[#c2410c]"
                                    animate={{ width: `${((active + 1) / 4) * 100}%` }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                />
                            </div>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`label-${active}`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="mt-3 font-mono text-[12px] uppercase tracking-[0.18em] text-[#59524a]"
                                >
                                    {PAINS[active].title}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* Stacking artifact deck */}
                <div className="lg:col-span-7 space-y-6 lg:space-y-14">
                    {PAINS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.id}
                                data-testid={`problem-artifact-${p.id}`}
                                onViewportEnter={() => setActive(i)}
                                viewport={{ amount: 0.55 }}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: EASE }}
                                className="lg:sticky rounded-2xl border border-[#1c1917]/10 bg-white p-5 sm:p-7 shadow-[0_24px_60px_-30px_rgba(28,25,23,0.28)]"
                                style={{ top: `${118 + i * 18}px` }}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#a29a8e] truncate">
                                        {String(i + 1).padStart(2, "0")} — {p.title}
                                    </span>
                                    <Icon className="h-4 w-4 shrink-0 text-[#a29a8e]" strokeWidth={1.5} />
                                </div>
                                <div className="mt-5 sm:mt-6 flex min-h-[110px] sm:min-h-[120px] items-center justify-center max-w-md mx-auto">
                                    {p.visual}
                                </div>
                                <div className="mt-5 sm:mt-6 border-t border-[#1c1917]/[0.07] pt-4 text-[13.5px] sm:text-[14px] text-[#59524a]">
                                    {p.line}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Bridge into the dark story — a drawn thread */}
            <div className="relative mx-auto mt-20 sm:mt-24 flex max-w-7xl flex-col items-center px-6">
                <motion.div
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: EASE }}
                    className="h-14 sm:h-16 w-px origin-top bg-gradient-to-b from-[#c2410c]/60 to-[#1c1917]/60"
                />
                <FadeUp delay={0.3}>
                    <p className="mt-5 font-mono text-[11px] sm:text-[11.5px] uppercase tracking-[0.22em] text-[#59524a] text-center">
                        So we built the fix — inside the business
                    </p>
                </FadeUp>
            </div>
        </section>
    );
}
